import type { LearningResource, PassportCommand, PassportCriterion } from "@peer-start/platform-contracts";
import { Button, Card, CardHeader } from "@peer-start/ui";
import { CheckCircle2, Clock3 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LearningResourcePreview } from "./LearningResourcePreview.js";
import { LearningThumbnail } from "./LearningThumbnail.js";
import { learningPresentation } from "../data/learning-presentations.js";

export function StampLearning({
  employeeId,
  stampId,
  resources,
  criteria,
  completedResourceIds,
  recommendedResourceIds,
  pending,
  act,
  readOnly = false,
}: {
  employeeId: string;
  stampId: string;
  resources: LearningResource[];
  criteria: PassportCriterion[];
  completedResourceIds: Set<string>;
  recommendedResourceIds: Set<string>;
  pending: boolean;
  readOnly?: boolean;
  act: (command: PassportCommand) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);
  const start = async (resourceId: string) => {
    if (openId === resourceId) {
      setOpenId(null);
      return;
    }
    if (!readOnly && !completedResourceIds.has(resourceId))
      await act({ type: "stamp_learning_started", employeeId, stampId, resourceId });
    setOpenId(resourceId);
  };
  return (
    <Card className="scroll-mt-6" id="learning">
      <CardHeader title={t("passportVig.stamp.learning")} subtitle={t("passportVig.stamp.learningNote")} />
      <div className="mt-4 space-y-3">
        {resources.map((resource) => {
          const complete = completedResourceIds.has(resource.id);
          const recommended = recommendedResourceIds.has(resource.id);
          const open = openId === resource.id;
          const contentId = `learning-preview-${resource.id}`;
          const presentation = learningPresentation(resource);
          const format =
            presentation.format === "video" && presentation.vimeoVideos.length
              ? "vimeo"
              : presentation.format;
          return (
            <article className="rounded-xl border border-border/70 p-4" key={resource.id}>
              <div className="flex flex-wrap items-start gap-3">
                <div aria-hidden="true" className="w-24 shrink-0 sm:w-32">
                  <LearningThumbnail
                    presentation={presentation}
                    resourceId={resource.id}
                    title={resource.title}
                  />
                </div>
                <div className="min-w-40 flex-1">
                  <h3 className="font-medium">{resource.title}</h3>
                  <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                    <span>{t(`passportVig.lesson.formats.${format}`)}</span>
                    <Clock3 aria-hidden="true" className="size-3" />
                    {t("common.minutes", { count: resource.minutes })}
                  </p>
                  {complete ? (
                    <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-success-foreground">
                      <CheckCircle2 aria-hidden="true" className="size-4" />
                      {t("passportVig.lesson.completed")}
                    </p>
                  ) : recommended ? (
                    <p className="mt-2 text-xs font-medium text-primary">
                      {t("passportVig.stamp.recommendedLearning")}
                    </p>
                  ) : null}
                </div>
                <Button
                  aria-controls={contentId}
                  aria-expanded={open}
                  className="w-full sm:ml-auto sm:w-auto"
                  controlId={`passport.learning.${resource.id}.start`}
                  disabled={pending}
                  onClick={() => void start(resource.id)}
                  variant="secondary"
                >
                  {t(
                    open
                      ? "passportVig.lesson.close"
                      : readOnly || complete
                        ? "learningRoute.viewResource"
                        : "passportVig.actions.startLearning",
                  )}
                </Button>
              </div>
              <div hidden={!open} id={contentId}>
                {open ? (
                  <div className="mt-4 space-y-5 border-t border-border/70 pt-4">
                    <LearningResourcePreview
                      criteria={criteria}
                      presentation={presentation}
                      resource={resource}
                    />
                    {!readOnly && !complete ? (
                      <Button
                        controlId={`passport.learning.${resource.id}.complete`}
                        disabled={pending}
                        onClick={() =>
                          void act({
                            type: "stamp_learning_completed",
                            employeeId,
                            stampId,
                            resourceId: resource.id,
                          })
                        }
                      >
                        {t("passportVig.actions.completeLearning")}
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </Card>
  );
}
